package com.schoolconnect.app.feature.notices

import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.request.NoticeCreateRequest
import com.schoolconnect.app.datasource.network.request.NoticeUpdateRequest
import com.schoolconnect.app.datasource.network.response.NoticeResponse
import com.schoolconnect.app.designsystem.appDateInputComparable
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.designsystem.currentAppDateLabel
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.notices.NoticesListRepository
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock

class NoticesViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var listJob: Job? = null
    private var statisticsJob: Job? = null
    private var saveJob: Job? = null
    private var viewJob: Job? = null
    private val schoolId = UserSessionStore.getHomeRoleSchoolId()
    private val currentUserId = UserSessionStore.getUserId()
    private val _uiState = MutableStateFlow(NoticesUiState())
    val uiState: StateFlow<NoticesUiState> = _uiState.asStateFlow()

    init {
        loadPage()
        loadStatistics()
        loadAudienceOptions()
        loadStatusOptions()
        loadClassTargetOptions()
    }

    private fun loadAudienceOptions() {
        scope.launch {
            loadLookupOptions("NOTICE_AUDIENCE").onSuccess { options ->
                _uiState.update { it.copy(audienceOptions = options) }
            }
        }
    }

    private fun loadStatusOptions() {
        scope.launch {
            loadLookupOptions("NOTICE_STATUS").onSuccess { options ->
                _uiState.update { it.copy(statusOptions = options) }
            }
        }
    }

    private suspend fun loadLookupOptions(category: String): Result<List<NoticeAudienceOption>> =
        SystemLookupRepository.loadCategoryItems(category).map { items ->
            items.mapNotNull { item ->
                val code = item.code?.trim()?.takeIf { it.isNotBlank() }
                val label = item.displayValue?.trim()?.takeIf { it.isNotBlank() }
                if (code != null && label != null) NoticeAudienceOption(code, label) else null
            }
        }

    private fun loadClassTargetOptions() {
        if (schoolId.isBlank()) return
        scope.launch {
            ClassSectionRepository.loadSchoolClassSectionLookup(schoolId).onSuccess { sections ->
                val options = sections
                    .sortedWith { left, right -> compareClassSections(left.className, left.sectionName, right.className, right.sectionName) }
                    .mapNotNull { section ->
                    val id = section.id?.trim()?.takeIf { it.isNotBlank() }
                    val className = section.className?.trim().orEmpty()
                    val sectionName = section.sectionName?.trim().orEmpty()
                    val label = listOf(className, sectionName).filter { it.isNotBlank() }.joinToString("-")
                    if (id != null && label.isNotBlank()) NoticeClassTargetOption(id, label) else null
                    }
                _uiState.update { it.copy(classTargetOptions = options) }
            }
        }
    }

    private fun compareClassSections(leftClass: String?, leftSection: String?, rightClass: String?, rightSection: String?): Int {
        val leftClassName = leftClass?.trim().orEmpty()
        val rightClassName = rightClass?.trim().orEmpty()
        val leftNumber = leftClassName.toIntOrNull()
        val rightNumber = rightClassName.toIntOrNull()
        val classComparison = when {
            leftNumber != null && rightNumber != null -> leftNumber.compareTo(rightNumber)
            leftNumber != null -> -1
            rightNumber != null -> 1
            else -> leftClassName.compareTo(rightClassName, ignoreCase = true)
        }
        if (classComparison != 0) return classComparison
        return leftSection.orEmpty().trim().compareTo(rightSection.orEmpty().trim(), ignoreCase = true)
    }

    fun onEvent(event: NoticesUiEvent) {
        when (event) {
            NoticesUiEvent.BackClicked -> Unit
            NoticesUiEvent.RefreshClicked -> {
                loadPage(forceRemote = true)
                loadStatistics(forceRemote = true)
            }
            is NoticesUiEvent.SearchChanged -> {
                _uiState.update { it.copy(searchQuery = event.query, pageIndex = 0) }
                loadPage(debounce = true)
            }
            is NoticesUiEvent.FiltersChanged -> {
                _uiState.update { it.copy(filters = event.filters, pageIndex = 0) }
                loadPage()
            }
            NoticesUiEvent.FiltersReset -> {
                _uiState.update { it.copy(searchQuery = "", filters = NoticeFilters(), pageIndex = 0) }
                loadPage()
            }
            is NoticesUiEvent.PageChanged -> {
                _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }
                loadPage()
            }
            is NoticesUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(pageSize = event.pageSize.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0) }
                loadPage()
            }
            is NoticesUiEvent.SortChanged -> {
                _uiState.update {
                    val ascending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(sortField = event.field, sortAscending = ascending, pageIndex = 0)
                }
                loadPage()
            }
            is NoticesUiEvent.CreateNotice -> createNotice(event.draft)
            is NoticesUiEvent.UpdateNotice -> updateNotice(event.noticeId, event.draft)
            is NoticesUiEvent.ViewNotice -> loadNoticeDetail(event.noticeId)
            is NoticesUiEvent.DeleteNotice -> deleteNotice(event.noticeId)
        }
    }

    private fun loadNoticeDetail(noticeId: String) {
        val normalizedId = noticeId.trim()
        if (normalizedId.isBlank()) return
        viewJob?.cancel()
        viewJob = scope.launch {
            _uiState.update { it.copy(viewingNotice = null, isViewingNotice = true, viewNoticeError = "") }
            NoticesListRepository.loadDetail(normalizedId).fold(
                onSuccess = {
                    val detail = loadAttachmentDetails(it.toDetailUi())
                    _uiState.update { state -> state.copy(viewingNotice = detail, isViewingNotice = false) }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] NoticesViewModel loadNoticeDetail failure noticeId=$normalizedId", throwable)
                    _uiState.update { it.copy(isViewingNotice = false, viewNoticeError = throwable.message.orEmpty().ifBlank { "Unable to load notice details." }) }
                }
            )
        }
    }

    private fun createNotice(draft: NoticeDraft) {
        if (_uiState.value.isSaving) return
        val title = draft.title.trim()
        val content = draft.content.trim()
        val expiryInput = draft.expiresAt.trim()
        val expiresAt = expiryInput.takeIf { it.isNotBlank() }?.let(::parseDisplayDate)
        val error = when {
            schoolId.isBlank() -> "A school context is required."
            title.isBlank() -> "Enter a notice title."
            content.isBlank() -> "Enter notice content."
            draft.audienceCode() == "CLASS" && draft.classTargetIds.isEmpty() -> "Select at least one class for this audience."
            expiryInput.isNotBlank() && expiresAt == null -> "Enter a valid expiry date in DD MMM YYYY format."
            expiryInput.isBeforeToday() -> "Expiry date cannot be earlier than today."
            else -> null
        }
        if (error != null) {
            _uiState.update { it.copy(saveErrorMessage = error) }
            return
        }
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            val lookupCodes = resolveNoticeLookupCodes(draft.isActive, draft.audience)
                .getOrElse { throwable ->
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Notice lookup values are unavailable.") }
                    return@launch
                }
            NoticesListRepository.create(
                NoticeCreateRequest(
                    schoolId = schoolId,
                    title = title,
                    content = content,
                    status = lookupCodes.first,
                    audience = lookupCodes.second,
                    classTargets = draft.classTargetIds.takeIf { lookupCodes.second == "CLASS" },
                    expiresAt = expiresAt?.let { "${it}T23:59:59Z" },
                    attachmentFileIds = draft.attachmentFileIds
                ),
                currentUserId = currentUserId
            ).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = "", saveGeneration = it.saveGeneration + 1) }
                    loadPage(forceRemote = true)
                    loadStatistics(forceRemote = true)
                },
                onFailure = { throwable ->
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to create notice.") }
                }
            )
        }
    }

    private fun updateNotice(noticeId: String, draft: NoticeDraft) {
        val title = draft.title.trim()
        val content = draft.content.trim()
        val expiry = draft.expiresAt.trim().takeIf { it.isNotBlank() }?.let(::parseDisplayDate)
        val wasPublished = draft.originalStatus?.let { !it.equals("Draft", ignoreCase = true) } == true
        val error = when {
            noticeId.isBlank() -> "A notice identifier is required."
            wasPublished && !draft.isActive -> "A published notice cannot be reverted to draft."
            title.isBlank() -> "Enter a notice title."
            content.isBlank() -> "Enter notice content."
            draft.audienceCode() == "CLASS" && draft.classTargetIds.isEmpty() -> "Select at least one class for this audience."
            draft.expiresAt.isNotBlank() && expiry == null -> "Enter a valid expiry date in DD MMM YYYY format."
            draft.expiresAt.isBeforeToday() -> "Expiry date cannot be earlier than today."
            else -> null
        }
        if (error != null) { _uiState.update { it.copy(saveErrorMessage = error) }; return }
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            val lookupCodes = resolveNoticeLookupCodes(draft.isActive, draft.audience)
                .getOrElse { throwable ->
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Notice lookup values are unavailable.") }
                    return@launch
                }
            NoticesListRepository.update(noticeId, NoticeUpdateRequest(
                title = title,
                content = content,
                audience = lookupCodes.second,
                classTargets = draft.classTargetIds.takeIf { lookupCodes.second == "CLASS" },
                expiresAt = expiry?.let { "${it}T23:59:59Z" },
                status = lookupCodes.first,
                isActive = draft.isActive,
                attachmentFileIds = draft.attachmentFileIds
            )).fold(
                onSuccess = { _uiState.update { it.copy(isSaving = false, saveErrorMessage = "", saveGeneration = it.saveGeneration + 1) }; loadPage(true); loadStatistics(true) },
                onFailure = { throwable -> _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to update notice.") } }
            )
        }
    }

    private fun deleteNotice(noticeId: String) {
        if (noticeId.isBlank()) return
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            NoticesListRepository.delete(noticeId).fold(
                onSuccess = { _uiState.update { it.copy(isSaving = false, saveGeneration = it.saveGeneration + 1) }; loadPage(true); loadStatistics(true) },
                onFailure = { throwable -> _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to delete notice.") } }
            )
        }
    }

    private fun loadPage(forceRemote: Boolean = false, debounce: Boolean = false) {
        listJob?.cancel()
        listJob = scope.launch {
            if (debounce) delay(300)
            val state = _uiState.value
            val publishedRange = state.filters.toApiPublishedRange()
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            NoticesListRepository.loadPage(
                search = state.searchQuery,
                audience = state.filters.audience.takeUnless { it.isBlank() },
                status = state.filters.status.toNoticeStatusApiFilter(),
                publishedFrom = publishedRange.first,
                publishedTo = publishedRange.second,
                pageIndex = state.pageIndex,
                pageSize = state.pageSize,
                sortBy = state.sortField.toApiSortField(),
                order = if (state.sortAscending) "asc" else "desc",
                forceRemote = forceRemote,
                refreshScope = scope
            ).fold(
                onSuccess = { result ->
                    val rows = result.items.map { it.toUi() }
                    _uiState.update { current -> current.copy(rows = rows, totalCount = result.totalCount, isLoading = false, errorMessage = "") }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] NoticesViewModel loadPage failure forceRemote=$forceRemote", throwable)
                    _uiState.update { it.copy(rows = emptyList(), totalCount = 0, isLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load notices." }) }
                }
            )
        }
    }

    private fun loadStatistics(forceRemote: Boolean = false) {
        statisticsJob?.cancel()
        statisticsJob = scope.launch {
            _uiState.update { it.copy(isStatisticsLoading = true) }
            NoticesListRepository.loadStatistics().fold(
                onSuccess = { response ->
                    val counters = response.counters
                    _uiState.update { it.copy(totalNotices = counters.counter("total", "total_notices") ?: 0, draftNotices = counters.counter("draft", "draft_notices") ?: 0, activeNotices = counters.counter("active", "active_notices") ?: 0, expiredNotices = counters.counter("expired", "expired_notices") ?: 0, isStatisticsLoading = false) }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] NoticesViewModel loadStatistics failure forceRemote=$forceRemote", throwable)
                    _uiState.update { it.copy(isStatisticsLoading = false, errorMessage = it.errorMessage.ifBlank { throwable.message.orEmpty().ifBlank { "Unable to load notice statistics." } }) }
                }
            )
        }
    }

    fun clear() {
        listJob?.cancel()
        statisticsJob?.cancel()
        saveJob?.cancel()
        viewJob?.cancel()
        scope.cancel()
    }

    private fun parseDisplayDate(value: String): String? {
        val parts = value.trim().split(" ").filter { it.isNotBlank() }
        if (parts.size != 3) return null
        val month = mapOf("JAN" to "01", "FEB" to "02", "MAR" to "03", "APR" to "04", "MAY" to "05", "JUN" to "06", "JUL" to "07", "AUG" to "08", "SEP" to "09", "OCT" to "10", "NOV" to "11", "DEC" to "12")[parts[1].uppercase()] ?: return null
        val day = parts[0].toIntOrNull() ?: return null
        val year = parts[2].toIntOrNull() ?: return null
        if (day !in 1..31 || year !in 1900..2200) return null
        return "${year.toString().padStart(4, '0')}-$month-${day.toString().padStart(2, '0')}"
    }

    private fun String.isBeforeToday(): Boolean =
        if (isBlank()) false else {
            val value = appDateInputComparable(this)
            val today = appDateInputComparable(currentAppDateLabel())
            value != null && today != null && value < today
        }

    private suspend fun resolveNoticeLookupCodes(isActive: Boolean, audience: String): Result<Pair<String, String>> {
        val statusLabel = if (isActive) "Published" else "Draft"
        val statusCode = resolveLookupCode("NOTICE_STATUS", statusLabel).getOrElse { return Result.failure(it) }
        val audienceCode = resolveLookupCode("NOTICE_AUDIENCE", audience).getOrElse { return Result.failure(it) }
        return Result.success(statusCode to audienceCode)
    }

    private suspend fun resolveLookupCode(category: String, displayValue: String): Result<String> {
        val normalized = displayValue.trim()
        if (normalized.isBlank()) return Result.failure(IllegalStateException("A value is required for lookup category '$category'."))
        val items = SystemLookupRepository.loadCategoryItems(category).getOrElse { return Result.failure(it) }
        val match = items.firstOrNull { item ->
            item.code.equals(normalized, ignoreCase = true) ||
                item.displayValue.equals(normalized, ignoreCase = true) ||
                (category == "NOTICE_AUDIENCE" && item.displayValue.orEmpty().startsWith("$normalized ", ignoreCase = true))
        }
        return match?.code?.trim()?.takeIf { it.isNotBlank() }?.let(Result.Companion::success)
            ?: Result.failure(IllegalStateException("Server value '$normalized' was not found in lookup category '$category'."))
    }
}

internal fun NoticesSortField.toApiSortField(): String = when (this) {
    NoticesSortField.ID -> "notice_id"
    NoticesSortField.TITLE -> "title"
    NoticesSortField.AUDIENCE -> "audience"
    NoticesSortField.PUBLISHED_DATE -> "created_at"
    NoticesSortField.EXPIRY_DATE -> "expires_at"
    NoticesSortField.STATUS -> "status"
}

internal fun String.toNoticeStatusApiFilter(): String? = trim()
    .takeUnless { it.isBlank() || it.equals("All", ignoreCase = true) }
    ?.uppercase()

internal fun NoticeFilters.toApiPublishedRange(): Pair<String?, String?> {
    val customFrom = appDateInputToApi(publishedDateFrom)
    val customTo = appDateInputToApi(publishedDateTo)
    if (publishedDate.equals("Custom Range", ignoreCase = true)) return customFrom to customTo

    val today = kotlin.time.Clock.System.now().toLocalDateTime(TimeZone.UTC).date
    return when (publishedDate.trim().lowercase()) {
        "today" -> today.toString() to today.toString()
        "last 7 days" -> today.minus(6, DateTimeUnit.DAY).toString() to today.toString()
        "this month" -> LocalDate(today.year, today.month, 1).toString() to today.toString()
        else -> null to null
    }
}

private fun NoticeDraft.audienceCode(): String = audience.trim().uppercase()

private fun Map<String, Int>.counter(vararg names: String): Int? = names.firstNotNullOfOrNull { name -> this[name] ?: this[name.lowercase()] ?: this[name.uppercase()] }

private fun NoticeResponse.toUi(): NoticeRowUi = NoticeRowUi(
    id = noticeId.orEmpty().ifBlank { id.orEmpty().ifBlank { "—" } },
    backendId = id.orEmpty().ifBlank { noticeId.orEmpty() },
    title = title.orEmpty().ifBlank { "Untitled notice" },
    audience = audience.orEmpty().ifBlank { "—" },
    publishedOn = createdAt.toDisplayDate(),
    expiryDate = expiresAt.toDisplayDate(),
    status = noticeStatusLabel(status, isActive, expiresAt),
    hasAttachments = !attachmentFileIds.isNullOrEmpty() || !attachmentFileUrls.isNullOrEmpty(),
    attachmentFileIds = attachmentFileIds.orEmpty(),
    attachmentFileUrls = attachmentFileUrls.orEmpty()
)

private fun NoticeResponse.toDetailUi(): NoticeDetailUi = NoticeDetailUi(
    id = noticeId.orEmpty().ifBlank { id.orEmpty().ifBlank { "—" } },
    title = title.orEmpty().ifBlank { "Untitled notice" },
    content = content.orEmpty(),
    audience = audience.orEmpty().ifBlank { "—" },
    expiryDate = expiresAt.toDisplayDate(),
    status = noticeStatusLabel(status, isActive, expiresAt),
    createdAt = createdAt.toDisplayTimestamp(),
    updatedAt = updatedAt.toDisplayTimestamp(),
    createdBy = createdByName.orEmpty().ifBlank { "Not available" },
    updatedBy = updatedByName.orEmpty().ifBlank { "Not available" },
    attachmentFileIds = attachmentFileIds.orEmpty(),
    attachmentFileUrls = attachmentFileUrls.orEmpty(),
    attachments = attachmentFileIds.orEmpty().indices.map { index ->
        NoticeAttachmentDetailUi(
            fileId = attachmentFileIds.orEmpty()[index],
            url = attachmentFileUrls.orEmpty().getOrNull(index).orEmpty(),
            name = "Attachment ${index + 1}",
            mimeType = "application/octet-stream",
            sizeLabel = "File"
        )
    }.ifEmpty {
        attachmentFileUrls.orEmpty().indices.map { index ->
            NoticeAttachmentDetailUi("", attachmentFileUrls.orEmpty()[index], "Attachment ${index + 1}", "application/octet-stream", "File")
        }
    },
    classTargetIds = classTargets.orEmpty()
)

private suspend fun loadAttachmentDetails(detail: NoticeDetailUi): NoticeDetailUi {
    if (detail.attachments.isEmpty()) return detail
    val enriched = detail.attachments.map { attachment ->
        val metadata = attachment.fileId.takeIf { it.isNotBlank() }?.let { FileUploadRepository.getMetadata(it).getOrNull() }
        val mimeType = metadata?.detectedMimeType ?: metadata?.declaredMimeType ?: attachment.mimeType
        val name = metadata?.originalFilename.orEmpty().ifBlank { attachment.name }
        val url = attachment.url.ifBlank {
            attachment.fileId.takeIf { it.isNotBlank() }?.let { FileUploadRepository.getDownloadUrl(it).getOrNull()?.url }.orEmpty()
        }
        val previewBytes = if (mimeType.startsWith("image/", ignoreCase = true) && url.isNotBlank()) {
            FileUploadRepository.downloadBytes(url).getOrNull()
        } else null
        attachment.copy(
            url = url,
            name = name,
            mimeType = mimeType,
            sizeLabel = metadata?.sizeBytes?.let(::formatAttachmentSize) ?: attachment.sizeLabel,
            previewBytes = previewBytes
        )
    }
    return detail.copy(attachments = enriched)
}

internal fun noticeStatusLabel(status: String?, isActive: Boolean?, expiresAt: String?, now: String = Clock.System.now().toString()): String = when {
    status.equals("DRAFT", ignoreCase = true) -> "Draft"
    status.equals("ARCHIVED", ignoreCase = true) -> "Archived"
    status.equals("EXPIRED", ignoreCase = true) -> "Expired"
    status.isNullOrBlank() && isActive != true -> "Draft"
    !expiresAt.isNullOrBlank() && expiresAt < now -> "Expired"
    else -> "Active"
}

private fun formatAttachmentSize(bytes: Long): String = when {
    bytes >= 1024L * 1024L -> "${bytes / (1024L * 1024L)} MB"
    bytes >= 1024L -> "${bytes / 1024L} KB"
    else -> "$bytes B"
}

private fun String?.toDisplayDate(): String {
    val value = this.orEmpty().trim()
    val parts = value.substringBefore('T').split('-')
    if (parts.size != 3) return value.ifBlank { "—" }
    val month = parts[1].toIntOrNull() ?: return value
    val label = listOf("JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC").getOrNull(month - 1) ?: return value
    return "${parts[2].padStart(2, '0')} $label ${parts[0]}"
}

private fun String?.toDisplayTimestamp(): String {
    val value = this.orEmpty().trim()
    if (value.isBlank()) return "—"

    // API timestamps are instants. Convert them to the browser/app timezone
    // before rendering instead of stripping the UTC marker from the raw value.
    val local = runCatching {
        Instant.parse(value).toLocalDateTime(TimeZone.currentSystemDefault())
    }.getOrElse {
        // Keep compatibility with APIs that return an ISO timestamp without
        // an offset; those values are treated as UTC by convention.
        runCatching {
            LocalDateTime.parse(value).toInstant(TimeZone.UTC)
                .toLocalDateTime(TimeZone.currentSystemDefault())
        }.getOrNull()
    }

    if (local == null) return value.substringBefore('T').toDisplayDate()

    val month = listOf("JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC")
        .getOrNull(local.month.ordinal)
        ?: return value.substringBefore('T').toDisplayDate()
    val hour = when (val normalized = local.hour % 12) {
        0 -> 12
        else -> normalized
    }
    val suffix = if (local.hour < 12) "AM" else "PM"
    return "${local.day.toString().padStart(2, '0')} $month ${local.year}, ${hour.toString().padStart(2, '0')}:${local.minute.toString().padStart(2, '0')} $suffix"
}
